import {MO_STATUS_DISPLAY} from '../../common/constants/MOs.js'
import FormattedDate from '../../common/components/FormattedDate.js'
import ButtonLink from '../../common/components/Button/ButtonLink.js'
import Address from '../../common/components/Order/Address.js'
import Timeline from '../../common/components/Details/Timeline/index.js'
import {
  createMOSelector,
  createMOCommentGroupsSelector,
  createMOWarehouseSelector,
  ensureMOComments,
} from '../../data/mos.js'
import MOTags from '../MOListPage/MOTags.js'
import {showAddMOCommentModal} from '../MOListPage/Modals/AddMOCommentModal.js'
import {
  showMOManufacturerNotesModal,
  showMOInternalNotesModal,
  showMOWarehouseModal,
  showMOShippingAmountModal,
  showMOTaxAmountModal,
} from '../MOListPage/Modals/EditMOParamModal.js'
import {moDetailReferenceIDSelector} from './moDetailSelectors.js'
import Lines from './Lines.js'
import {useMemo} from 'react'
import {useSelector} from '../../store.js'
import Currency from '../../common/components/Currency.js'
import Icon from '../../common/icons/Icon.js'

export default function Details() {
  const referenceID = useSelector(moDetailReferenceIDSelector)
  const moSelector = useMemo(() => createMOSelector(referenceID), [referenceID])
  const commentGroupsSelector = useMemo(
    () => createMOCommentGroupsSelector(moSelector),
    [moSelector],
  )
  const moWarehouseSelector = useMemo(
    () => createMOWarehouseSelector(moSelector),
    [moSelector],
  )
  const mo = useSelector(moSelector)
  const warehouse = useSelector(moWarehouseSelector)
  const totalUnitCost = useMemo(
    () =>
      mo.lines.reduce(
        (prev, {unit_cost, finished_quantity}) =>
          prev + finished_quantity * unit_cost,
        0,
      ),
    [mo],
  )
  const totalLaborUnitCost = useMemo(
    () =>
      mo.lines.reduce(
        (prev, {labor_unit_cost, finished_quantity}) =>
          prev + finished_quantity * labor_unit_cost,
        0,
      ),
    [mo],
  )
  const grandTotalCost =
    totalUnitCost + totalLaborUnitCost + mo.shipping_amount + mo.tax_amount

  return (
    <>
      <div className="flex margin-bottom-25">
        <h2 className="page__title page__title--inner inline-block v-align-middle margin-right-15">
          <strong>{referenceID}</strong>
        </h2>
        {mo.tags.length !== 0 && (
          <div className="inline-block v-align-middle">
            <MOTags referenceID={referenceID} tags={mo.tags} />
          </div>
        )}
      </div>
      <div className="flex">
        <div className="medium-3 columns wrap__list--order-data padding-left-0">
          <dl className="list list--order-data">
            <dt className="list__title--order-data">MFG Order Summary</dt>
            <dd className="list__item--order-data">
              <strong>Created: </strong>
              <span className="fs-n1">
                <FormattedDate value={mo.created_date} format="MMM D, YYYY" />
                {' at '}
                <FormattedDate value={mo.created_date} format="h:mma" />
              </span>
            </dd>
            <dd className="list__item--order-data">
              <strong>Updated: </strong>
              <span className="fs-n1">
                <FormattedDate value={mo.updated_date} format="MMM D, YYYY" />
                {' at '}
                <FormattedDate value={mo.updated_date} format="h:mma" />
              </span>
            </dd>
            <dd className="list__item--order-data">
              <strong>Status: </strong>
              <span className="fs-n1">{MO_STATUS_DISPLAY[mo.status]}</span>
            </dd>
            <dd className="list__item--order-data">
              <strong>Shipping Cost: </strong>
              <ButtonLink
                className="fs-n1 v-align-base"
                onClick={() => showMOShippingAmountModal(referenceID)}
              >
                <Currency value={mo.shipping_amount} />
              </ButtonLink>
            </dd>
            <dd className="list__item--order-data">
              <strong>Tax: </strong>
              <ButtonLink
                className="fs-n1 v-align-base"
                onClick={() => showMOTaxAmountModal(referenceID)}
              >
                <Currency value={mo.tax_amount} />
              </ButtonLink>
            </dd>
            <dd className="list__item--order-data">
              <strong>Running Total Unit Cost: </strong>
              <span className="fs-n1 margin-right-3">
                <Currency value={totalUnitCost} />
              </span>
              <span
                className="tooltip--top fs-00 lh-md v-align-base"
                data-hint="Running total unit cost will update when a unit has been finished"
              >
                <Icon
                  name="info-circle"
                  className="v-align-middle text--lt-grey"
                />
              </span>
            </dd>
            <dd className="list__item--order-data">
              <strong>Running Total Labor Unit Cost: </strong>
              <span className="fs-n1 margin-right-3">
                <Currency value={totalLaborUnitCost} />
              </span>
              <span
                className="tooltip--top fs-00 lh-md v-align-base"
                data-hint="Running total labor unit cost will update when a unit has been finished"
              >
                <Icon
                  name="info-circle"
                  className="v-align-middle text--lt-grey"
                />
              </span>
            </dd>
            <dd className="list__item--order-data margin-top-1">
              <strong>Running Total Cost: </strong>
              <span className="fs-n1">
                <Currency value={grandTotalCost} />
              </span>
            </dd>
          </dl>
          <dl className="list list--order-data with-top-divider">
            <dt className="list__title--order-data">Notes for Manufacturer</dt>
            {mo.manufacturer_notes ? (
              <dd className="list__item--order-data">
                <div className="ws-pre-wrap">{mo.manufacturer_notes}</div>
                <ButtonLink
                  className="meta-modal-button-edit-manufacturer-note"
                  onClick={() => showMOManufacturerNotesModal(referenceID)}
                >
                  Edit
                </ButtonLink>
              </dd>
            ) : (
              <dd className="list__item--order-data">
                <ButtonLink
                  className="meta-modal-button-add-manufacturer-note"
                  onClick={() => showMOManufacturerNotesModal(referenceID)}
                >
                  Add note
                </ButtonLink>
              </dd>
            )}
          </dl>
          <dl className="list list--order-data with-top-divider">
            <dt className="list__title--order-data">Internal Notes</dt>
            {mo.internal_notes ? (
              <dd className="list__item--order-data">
                <div className="ws-pre-wrap">{mo.internal_notes}</div>
                <ButtonLink
                  className="meta-modal-button-edit-internal-note"
                  onClick={() => showMOInternalNotesModal(referenceID)}
                >
                  Edit
                </ButtonLink>
              </dd>
            ) : (
              <dd className="list__item--order-data">
                <ButtonLink
                  className="meta-modal-button-add-internal-note"
                  onClick={() => showMOInternalNotesModal(referenceID)}
                >
                  Add note
                </ButtonLink>
              </dd>
            )}
          </dl>
        </div>
        <div className="medium-4 columns wrap__list--order-data">
          <Address
            title="Assigned Warehouse"
            address={warehouse.address}
            onEdit={() => showMOWarehouseModal(referenceID)}
            noPhone
            noEmail
          />
        </div>
      </div>
      <hr className="hr--detail-page" />
      <Lines />
      <hr className="hr--detail-page" />
      <div className="margin-top-20 margin-bottom-15">
        <h3 className="fs-02 margin-bottom-0">
          <strong>Total Cost Summary</strong>
        </h3>
      </div>
      <div className="flex w-100">
        <div className="mw-15 divider--right padding-right-20 margin-right-20">
          <dl className="list">
            <dt className="list__title fs-00 lh-md margin-bottom-0">
              Shipping Cost
            </dt>
            <dd className="list__item margin-bottom-3">
              <strong className="fs-03 lh-md">
                <Currency value={mo.shipping_amount} />{' '}
              </strong>
            </dd>
            <dd className="list__item margin-bottom-0">
              <ButtonLink
                className="light lh-md fs-n1 flex--justify-col"
                onClick={() => showMOShippingAmountModal(referenceID)}
              >
                <span>Edit</span>
              </ButtonLink>
            </dd>
          </dl>
        </div>
        <div className="mw-15 divider--right padding-right-20 margin-right-20">
          <dl className="list">
            <dt className="list__title fs-00 lh-md margin-bottom-0">Tax</dt>
            <dd className="list__item margin-bottom-3">
              <strong className="fs-03 lh-md">
                <Currency value={mo.tax_amount} />
              </strong>
            </dd>
            <dd className="list__item margin-bottom-0">
              <ButtonLink
                className="light lh-md fs-n1 flex--justify-col"
                onClick={() => showMOTaxAmountModal(referenceID)}
              >
                <span>Edit</span>
              </ButtonLink>
            </dd>
          </dl>
        </div>
        <div className="mw-20 divider--right padding-right-20 margin-right-20">
          <dl className="list">
            <dt className="list__title fs-00 lh-md margin-bottom-0">
              <span className="margin-right-3">Running Total Unit Cost</span>
              <span
                className="tooltip--left fs-01 lh-md v-align-base"
                data-hint="Running total unit cost will update when a unit has been finished"
              >
                <Icon
                  name="info-circle"
                  className="v-align-middle text--lt-grey"
                />
              </span>
            </dt>
            <dd className="list__item margin-bottom-0">
              <strong className="fs-03 lh-md">
                <Currency value={totalUnitCost} />
              </strong>
            </dd>
          </dl>
        </div>
        <div className="mw-20 divider--right padding-right-20 margin-right-20">
          <dl className="list">
            <dt className="list__title fs-00 lh-md margin-bottom-0">
              <span className="margin-right-3">
                Running Total Labor Unit Cost
              </span>
              <span
                className="tooltip--left fs-01 lh-md v-align-base"
                data-hint="Running total labor unit cost will update when a unit has been finished"
              >
                <Icon
                  name="info-circle"
                  className="v-align-middle text--lt-grey"
                />
              </span>
            </dt>
            <dd className="list__item margin-bottom-0">
              <strong className="fs-03 lh-md">
                <Currency value={totalLaborUnitCost} />
              </strong>
            </dd>
          </dl>
        </div>
        <div className="mw-15">
          <dl className="list">
            <dt className="list__title fs-00 lh-md margin-bottom-0">
              Running Total Cost
            </dt>
            <dd className="list__item margin-bottom-0">
              <strong className="fs-03 lh-md">
                <Currency value={grandTotalCost} />
              </strong>
            </dd>
          </dl>
        </div>
      </div>
      <hr className="hr--detail-page" />
      <Timeline
        commentGroupsSelector={commentGroupsSelector}
        readOnly={false}
        addComment={() => showAddMOCommentModal(referenceID)}
        ensureComments={() => ensureMOComments(referenceID)}
      />
    </>
  )
}
